"use client";

import { useCallback, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  clearClosedConnections,
  getConnections,
  subscribeConnectionsStore,
  type ConnectionEntry,
} from "../stores/connections-store";

function stateColor(readyState: number): string {
  if (readyState === 1) return "text-emerald-500";
  if (readyState === 0) return "text-blue-400";
  return "text-muted-foreground";
}

function formatDuration(entry: ConnectionEntry): string {
  const end = entry.closedAt ?? Date.now();
  const sec = Math.floor((end - entry.openedAt) / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m${sec % 60}s`;
}

export function ConnectionsPanel() {
  const t = useTranslations("debug.connections");
  const conns = useSyncExternalStore(
    subscribeConnectionsStore,
    getConnections,
    getConnections,
  );

  const handleClear = useCallback(() => {
    clearClosedConnections();
  }, []);

  if (conns.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        {t("empty")}
      </div>
    );
  }

  const active = conns.filter((c) => !c.closedAt);
  const closed = conns.filter((c) => c.closedAt);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">
          {t("summary", { active: active.length, closed: closed.length })}
        </span>
        {closed.length > 0 && (
          <button
            onClick={handleClear}
            className="text-sm px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("clearClosed")}
          </button>
        )}
      </div>
      <div className="space-y-0.5">
        {conns.map((conn) => (
          <div
            key={conn.id}
            className={cn(
              "flex items-start gap-2 py-1.5 px-2 rounded text-sm hover:bg-muted/30 group",
              conn.closedAt && "opacity-50",
            )}
          >
            <span
              className={cn(
                "shrink-0 w-16 font-semibold",
                stateColor(conn.readyState),
              )}
            >
              {conn.readyState === 0
                ? t("states.connecting")
                : conn.readyState === 1
                  ? t("states.open")
                  : conn.readyState === 2
                    ? t("states.closed")
                    : t("states.unknown")}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-foreground/80 break-all">
                {conn.url}
              </div>
              <div className="text-muted-foreground/60 text-sm mt-0.5 flex gap-3">
                <span>{formatDuration(conn)}</span>
                <span>{t("messageCount", { count: conn.messageCount })}</span>
                {conn.errorCount > 0 && (
                  <span className="text-red-400">
                    {t("errorCount", { count: conn.errorCount })}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
